export class ApiError extends Error {
  status: number;
  code: string;
  requestId?: string;
  details?: unknown;
  retryAfter?: number;

  constructor(
    status: number,
    code: string,
    message: string,
    options: { requestId?: string; details?: unknown; retryAfter?: number } = {}
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = options.requestId;
    this.details = options.details;
    this.retryAfter = options.retryAfter;
  }
}

function cookieValue(name: string): string | undefined {
  const prefix = `${encodeURIComponent(name)}=`;
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix))
    ?.slice(prefix.length);
}

function workspaceHeaders(path: string): Record<string, string> {
  // Workspace administration must remain reachable even if a previously selected
  // workspace was removed or the current user's membership was revoked.
  if (path === "/api/v1/workspaces" || path.startsWith("/api/v1/workspaces/")) return {};
  const workspaceId = window.localStorage.getItem("ak_active_workspace") || "";
  const projectId = window.localStorage.getItem("ak_active_project") || "";
  return {
    ...(workspaceId ? { "X-AK-Workspace": workspaceId } : {}),
    ...(workspaceId && projectId ? { "X-AK-Project": projectId } : {})
  };
}

let sessionConfirmation: Promise<void> | null = null;

function confirmSessionBeforeExpiring(): void {
  if (sessionConfirmation) return;
  sessionConfirmation = (async () => {
    try {
      // A route-specific 401 (or a stale request completed after login) must
      // never sign the whole dashboard out. Only /auth/me is authoritative.
      const response = await fetch("/api/v1/auth/me", {
        credentials: "include",
        headers: { "Cache-Control": "no-store" }
      });
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent("ak:auth-expired"));
      }
    } catch {
      // A temporary network/proxy failure is not proof that the session ended.
    } finally {
      sessionConfirmation = null;
    }
  })();
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method || "GET").toUpperCase();
  const csrfToken = ["POST", "PUT", "PATCH", "DELETE"].includes(method)
    ? cookieValue("ak_csrf")
    : undefined;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort("timeout"), 45_000);
  const externalSignal = init.signal;
  const abortFromExternal = () => controller.abort(externalSignal?.reason);
  externalSignal?.addEventListener("abort", abortFromExternal, { once: true });
  let decodedCsrf: string | undefined;
  try {
    decodedCsrf = csrfToken ? decodeURIComponent(csrfToken) : undefined;
  } catch {
    decodedCsrf = undefined;
  }
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      signal: controller.signal,
      credentials: "include",
      headers: {
        ...(init.body && !isFormData ? { "Content-Type": "application/json" } : {}),
        ...(decodedCsrf ? { "X-CSRF-Token": decodedCsrf } : {}),
        ...workspaceHeaders(path),
        ...init.headers
      }
    });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new ApiError(408, "request_timeout", "La solicitud tardó demasiado. Inténtalo de nuevo.");
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
    externalSignal?.removeEventListener("abort", abortFromExternal);
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as {
      error?: { code?: string; message?: string; details?: unknown; request_id?: string };
      request_id?: string;
    } | null;
    const retryAfterHeader = Number(response.headers.get("Retry-After"));
    if (response.status === 401 && path !== "/api/v1/auth/me") confirmSessionBeforeExpiring();
    throw new ApiError(
      response.status,
      payload?.error?.code || "request_error",
      payload?.error?.message || `Error HTTP ${response.status}`,
      {
        details: payload?.error?.details,
        requestId: payload?.error?.request_id || payload?.request_id,
        retryAfter: Number.isFinite(retryAfterHeader) ? retryAfterHeader : undefined
      }
    );
  }
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("Content-Type") || "";
  if (!contentType.includes("json")) return await response.text() as T;
  return await response.json() as T;
}

export async function apiStream<T>(
  path: string,
  body: Record<string, unknown>,
  onDelta: (delta: string) => void,
  externalSignal?: AbortSignal
): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort("timeout"), 15 * 60_000);
  const abortFromExternal = () => controller.abort(externalSignal?.reason || "cancelled");
  externalSignal?.addEventListener("abort", abortFromExternal, { once: true });
  const csrfToken = cookieValue("ak_csrf");
  let decodedCsrf: string | undefined;
  try {
    decodedCsrf = csrfToken ? decodeURIComponent(csrfToken) : undefined;
  } catch {
    decodedCsrf = undefined;
  }
  let response: Response;
  try {
    response = await fetch(path, {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(decodedCsrf ? { "X-CSRF-Token": decodedCsrf } : {}),
        ...workspaceHeaders(path)
      },
      body: JSON.stringify({ ...body, stream: true })
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null) as {
        error?: { code?: string; message?: string; details?: unknown; request_id?: string };
      } | null;
      if (response.status === 401) confirmSessionBeforeExpiring();
      throw new ApiError(
        response.status,
        payload?.error?.code || "request_error",
        payload?.error?.message || `Error HTTP ${response.status}`,
        { details: payload?.error?.details, requestId: payload?.error?.request_id }
      );
    }
    if (!response.body) throw new ApiError(502, "stream_unavailable", "El servidor no abrió el stream.");
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let finalResponse: T | undefined;
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
        let boundary = buffer.indexOf("\n\n");
        while (boundary >= 0) {
          const record = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const data = record.split("\n").filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).trimStart()).join("\n");
          if (data) {
            const event = JSON.parse(data) as {
              type?: string;
              delta?: string;
              response?: T;
              error?: { code?: string; message?: string; request_id?: string };
            };
            if (event.type === "delta" && event.delta) onDelta(event.delta);
            if (event.type === "done" && event.response) finalResponse = event.response;
            if (event.type === "error") {
              throw new ApiError(
                502,
                event.error?.code || "stream_generation_failed",
                event.error?.message || "No se pudo completar la generación.",
                { requestId: event.error?.request_id }
              );
            }
          }
          boundary = buffer.indexOf("\n\n");
        }
      }
    } finally {
      reader.releaseLock();
    }
    if (!finalResponse) throw new ApiError(502, "stream_incomplete", "El stream terminó sin una respuesta final.");
    return finalResponse;
  } catch (error) {
    if (controller.signal.aborted) {
      if (externalSignal?.aborted) {
        throw new ApiError(499, "request_cancelled", "Generación cancelada.");
      }
      throw new ApiError(408, "request_timeout", "La generación superó el tiempo máximo.");
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
    externalSignal?.removeEventListener("abort", abortFromExternal);
  }
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("es-PE", { notation: value > 99_999 ? "compact" : "standard" }).format(value);
}

export function formatDate(value?: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("es", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export function formatLatency(value?: number | null): string {
  if (!value) return "—";
  return value >= 1_000 ? `${(value / 1_000).toFixed(1)} s` : `${Math.round(value)} ms`;
}
